import { faArrowRight, faFire } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontPopularItems } from '../../../services/portalDashboardService';
import { ItemCarouselSkeleton } from '../../skeletons';
import { ItemCarousel } from './ItemCarousel';

/**
 * Popular Items widget — horizontal-scrolling carousel of items with
 * the highest lifetime distinct-client purchase count for the active
 * brand. Anonymous-safe: works for logged-out visitors too, so it's
 * a good "what's hot here" surface for storefront homepages where
 * many visitors browse before signing in.
 */
export function PopularItemsWidget({
	title,
	limit,
	density,
}: {
	title?: string;
	limit?: number;
	density?: 'standard' | 'compact';
}): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();

	const queryKey = ['portal', 'dashboard', 'popular-items', limit ?? null];
	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () => getStorefrontPopularItems(token, limit),
		// Anonymous-safe — fetch regardless of token presence.
		staleTime: 5 * 60_000,
	});

	const items = data?.data ?? [];

	// Auto-hide when there's nothing to show. The catalog filter set
	// (active + non-hidden + brand company + client groups) can leave
	// the widget empty for a brand-new tenant or a client whose
	// groups don't intersect any popular items.
	if (!isLoading && !isError && items.length === 0) return null;

	return (
		<section className="w-full h-full flex flex-col">
			<header className="flex items-center justify-between mb-3">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon icon={faFire} className="text-portal-accent" />
					{t(title ?? 'Popular right now')}
				</h2>
				<button
					type="button"
					onClick={() => navigate('/catalog')}
					className="text-xs md:text-sm text-portal-accent hover:opacity-80 inline-flex items-center gap-1"
				>
					{t('Browse catalog')}
					<FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
				</button>
			</header>

			{isLoading ? (
				/* Shaped like the item strip below (PD-14692). */
				<ItemCarouselSkeleton />
			) : isError ? (
				<EmptyMessage
					message={t("We couldn't load popular items right now.")}
				/>
			) : (
				<ItemCarousel
					items={items}
					invalidateQueryKey={queryKey}
					density={density}
					emptyState={
						<EmptyMessage
							message={t(
								'No popular items to show yet — check back as the catalog gets more activity.',
							)}
						/>
					}
				/>
			)}
		</section>
	);
}

function EmptyMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}
